import {
  Action,
  Fieldset,
  Message,
  SimpleTable,
  Stack,
  Text,
} from '@doveit/bricks';
import objectHash from 'object-hash';
import React from 'react';
import { PASS_THROUGH_VALIDATION_SCHEMA } from '../../../../../constants';
import { useGeocodeByAddress } from '../../../../../hooks/use-geocode-by-address/useGeocodeByAddress';
import useIsDevice from '../../../../../hooks/use-is-device/useIsDevice';
import useLead from '../../../../../lead/hooks/use-lead/useLead';
import { AssignmentWizardData, PropertyUnit } from '../../../../../providers/api/dtos';
import PropertyGeoForm, { PropertyGeoFormModel } from '../../../../components/property-geo-form/PropertyGeoForm';
import UpsertPropertyUnitAction from '../../../../containers/upsert-property-unit-action/UpsertPropertyUnitAction';
import ViewPropertyUnitCard from '../../../../containers/view-property-unit-card/ViewPropertyUnitCard';
import ViewPropertyUnitTableRow from '../../../../containers/view-property-unit-table-row/ViewPropertyUnitTableRow';
import { AssignmentWizardStepProps } from '../types';
import { toAssignmentWizardData, toPropertyGeoFormModel } from './mappers';
import { useProspectDocuments } from '../../../../../prospect/hooks/use-prospect-documents/useProspectDocuments';
import { DocumentTypeValue } from '../../../../../domain/types';
import { useAiSuggestedCadastralRegistry } from '../../../../hooks/use-ai-suggested-cadastral-registry/useAiSuggestedCadastralRegistry';
import SuggestedPropertyUnitTableRow from '../../../../components/suggested-property-unit-table-row/SuggestedPropertyUnitTableRow';
import { isEqual } from '../../../../../utils/object/object';
import SuggestedPropertyUnitCard from '../../../../components/suggested-property-unit-card/SuggestedPropertyUnitCard';

export const FILL_CADASTRAL_DATA_INFO_MESSAGE = 'Compila i dati catastali e guadagna visibilità sui portali';

const AssignmentWizardProperty: React.FC<AssignmentWizardStepProps> = ({
  prospect,
  initialData,
  loading = false,
  formRef,
  onSubmit,
}) => {
  const { data: lead, isLoading: isLeadLoading } = useLead(prospect?.leadId);
  const { data: geocodeResult = [] } = useGeocodeByAddress(lead?.propertyAddress);
  const isMobile = useIsDevice('mobile');

  const { data: prospectDocuments } = useProspectDocuments(prospect?.id);

  const cadastralSurvey = React.useMemo(() => prospectDocuments?.find((doc) => doc.type === DocumentTypeValue.CADASTRAL_SURVEY), [prospectDocuments]);

  const [currentWizardData, setCurrentWizardData] = React.useState<AssignmentWizardData>(initialData);

  React.useEffect(() => {
    setCurrentWizardData(initialData);
  }, [initialData]);

  const suggestedCadastralRegistry = useAiSuggestedCadastralRegistry(currentWizardData.property, cadastralSurvey);

  const suggestedPropertyUnits = React.useMemo(() => {
    if (!suggestedCadastralRegistry?.suggestedUnits) return [];

    return suggestedCadastralRegistry.suggestedUnits.filter((suggestion) => {
      const suggestionKey = objectHash(suggestion.suggestion);
      return !currentWizardData.suggestions?.ignoredCadastralUnits?.includes(suggestionKey);
    });
  }, [suggestedCadastralRegistry, currentWizardData.suggestions]);

  const handlePropertyUnitCreate = React.useCallback((newPropertyUnit: PropertyUnit) => {
    setCurrentWizardData((current) => {
      if (current.property?.cadastralRegistry?.units?.some((unit) => isEqual(unit, newPropertyUnit, { loose: true }))) return current;

      const updatedUnits = current.property?.cadastralRegistry?.units ? [...current.property.cadastralRegistry.units, newPropertyUnit] : [newPropertyUnit];

      return {
        ...current,
        property: {
          ...current.property,
          cadastralRegistry: {
            ...current.property?.cadastralRegistry,
            units: updatedUnits,
          },
        },
      };
    });
  }, []);

  const handlePropertyUnitUpdate = React.useCallback((updatedPropertyUnit: PropertyUnit, oldPropertyUnit: PropertyUnit) => {
    setCurrentWizardData((current) => {
      const index = current.property?.cadastralRegistry?.units?.findIndex((unit) => isEqual(unit, oldPropertyUnit, { loose: true }));
      if (index === undefined || index === -1) return current;

      const updatedUnits = current.property?.cadastralRegistry?.units?.map((unit, i) => (i === index ? updatedPropertyUnit : unit));

      return {
        ...current,
        property: {
          ...current.property,
          cadastralRegistry: {
            ...current.property?.cadastralRegistry,
            units: updatedUnits,
          },
        },
      };
    });
  }, []);

  const handlePropertyUnitDelete = React.useCallback((deletedPropertyUnit: PropertyUnit) => {
    setCurrentWizardData((current) => {
      const updatedUnits = current.property?.cadastralRegistry?.units?.filter((unit) => !isEqual(unit, deletedPropertyUnit, { loose: true }));

      return {
        ...current,
        property: {
          ...current.property,
          cadastralRegistry: {
            ...current.property?.cadastralRegistry,
            units: updatedUnits,
          },
        },
      };
    });
  }, []);

  const handlePropertyUnitIgnore = React.useCallback((ignoredPropertyUnit: PropertyUnit) => {
    setCurrentWizardData((current) => {
      const ignoredUnitKey = objectHash(ignoredPropertyUnit);

      if (current.suggestions?.ignoredCadastralUnits?.includes(ignoredUnitKey)) return current;

      return {
        ...current,
        suggestions: {
          ignoredSellers: [],
          ...current.suggestions,
          ignoredCadastralUnits: [...(current.suggestions?.ignoredCadastralUnits ?? []), ignoredUnitKey],
        },
      };
    });
  }, []);

  const onGeoFormSubmit = React.useCallback(async (formValues: PropertyGeoFormModel) => {
    const newAssignmentWizardData = await toAssignmentWizardData(currentWizardData, formValues);

    /**
     * @todo fix the type in bricks
     */
    const isDirty = !isEqual(initialData, newAssignmentWizardData, { loose: true });

    onSubmit(newAssignmentWizardData, isDirty);
  }, [currentWizardData, initialData, onSubmit]);

  return (
    <Stack gap={400}>
      <Message
        type="info"
        message={FILL_CADASTRAL_DATA_INFO_MESSAGE}
      />

      <PropertyGeoForm
        initialValues={toPropertyGeoFormModel(initialData, lead?.propertyAddress, geocodeResult[0])}
        innerRef={formRef}
        validationSchema={PASS_THROUGH_VALIDATION_SCHEMA}
        onSubmit={onGeoFormSubmit}
        loading={loading || isLeadLoading}
      />

      <Fieldset
        legend="Dati catastali"
        actions={[
          <UpsertPropertyUnitAction
            onSuccess={handlePropertyUnitCreate}
          >
            {({ upsert }) => (
              <Action
                label="Aggiungi"
                aria-label="Pulsante per aggiungere una nuova unità immobiliare"
                size="S"
                onClick={upsert}
              />
            )}
          </UpsertPropertyUnitAction>,
        ]}
      >
        <Stack gap={400}>
          {suggestedPropertyUnits.length > 0 && (
            <Message
              type="warning"
              boxed
              message="Unità immobiliari suggerite"
              description={(
                <Text.BodySmall>
                  Le seguenti unità immobiliari sono state rilevate automaticamente dalla visura catastale. <br />
                  Puoi aggiungerle alla scheda immobile, confrontarle con unità già presenti o ignorarle.
                </Text.BodySmall>
              )}
            >
              {!isMobile && (
                <SimpleTable aria-label="Tabella unità immobiliari suggerite">
                  <SimpleTable.Body>
                    {suggestedPropertyUnits.map((suggestion) => (
                      <SimpleTable.Row
                        asChild
                        key={objectHash(suggestion)}
                      >
                        <SuggestedPropertyUnitTableRow
                          suggestedPropertyUnit={suggestion}
                          onAdd={handlePropertyUnitCreate}
                          onCompare={handlePropertyUnitUpdate}
                          onIgnore={handlePropertyUnitIgnore}
                        />
                      </SimpleTable.Row>
                    ))}
                  </SimpleTable.Body>
                </SimpleTable>
              )}

              {isMobile && (
                <Stack aria-label="Lista unità immobiliari suggerite">
                  {suggestedPropertyUnits.map((suggestion) => (
                    <SuggestedPropertyUnitCard
                      key={objectHash(suggestion)}
                      suggestedPropertyUnit={suggestion}
                      onAdd={handlePropertyUnitCreate}
                      onCompare={handlePropertyUnitUpdate}
                      onIgnore={handlePropertyUnitIgnore}
                    />
                  ))}
                </Stack>
              )}
            </Message>
          )}

          {!currentWizardData.property?.cadastralRegistry?.units?.length
            ? (
              <Message
                message="Aggiungi le unità immobiliari"
                description={(
                  <Text.BodySmall>
                    Aggiungi le unità immobiliari relative all&apos;immobile per aumentare la visibilità sui portali.
                  </Text.BodySmall>
                )}
              />
            )
            : (
              <Stack gap={150} aria-label="Lista unità">
                {isMobile && currentWizardData.property?.cadastralRegistry?.units && (
                  currentWizardData.property.cadastralRegistry.units.map((unit) => (
                    <ViewPropertyUnitCard
                      key={objectHash(unit)}
                      propertyUnit={unit}
                      canUpdate
                      canDelete
                      onUpdate={handlePropertyUnitUpdate}
                      onDelete={handlePropertyUnitDelete}
                    />
                  ))
                )}
                {!isMobile && currentWizardData.property?.cadastralRegistry?.units && (
                  <SimpleTable aria-label="Tabella dati catastali">
                    <SimpleTable.Header>
                      <SimpleTable.HeaderCell>
                        <Text.BodySmall>Sezione</Text.BodySmall>
                      </SimpleTable.HeaderCell>
                      <SimpleTable.HeaderCell>
                        <Text.BodySmall>Foglio</Text.BodySmall>
                      </SimpleTable.HeaderCell>
                      <SimpleTable.HeaderCell>
                        <Text.BodySmall>Part.</Text.BodySmall>
                      </SimpleTable.HeaderCell>
                      <SimpleTable.HeaderCell>
                        <Text.BodySmall>Sub.</Text.BodySmall>
                      </SimpleTable.HeaderCell>
                      <SimpleTable.HeaderCell>
                        <Text.BodySmall>Cat.</Text.BodySmall>
                      </SimpleTable.HeaderCell>
                      <SimpleTable.HeaderCell>
                        <Text.BodySmall>Dest. d&apos;uso</Text.BodySmall>
                      </SimpleTable.HeaderCell>
                      <SimpleTable.HeaderCell>
                        <Text.BodySmall>Rendita</Text.BodySmall>
                      </SimpleTable.HeaderCell>
                      <SimpleTable.HeaderCell>
                        <Text.BodySmall>Classe Energ.</Text.BodySmall>
                      </SimpleTable.HeaderCell>
                      <SimpleTable.HeaderCell />
                    </SimpleTable.Header>
                    <SimpleTable.Body>
                      {currentWizardData.property?.cadastralRegistry?.units?.map((unit) => (
                        <SimpleTable.Row key={objectHash(unit)} asChild>
                          <ViewPropertyUnitTableRow
                            propertyUnit={unit}
                            canUpdate
                            canDelete
                            onDelete={handlePropertyUnitDelete}
                            onUpdate={handlePropertyUnitUpdate}
                            padded
                          />
                        </SimpleTable.Row>
                      ))}
                    </SimpleTable.Body>
                  </SimpleTable>
                )}
              </Stack>
            )}
        </Stack>
      </Fieldset>
    </Stack>
  );
};

export default AssignmentWizardProperty;
